import React, { Suspense } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { isToday, isYesterday } from "date-fns";
import { Box, Text, Flex, Icon } from "@chakra-ui/react";
import {
  Loading,
  CreditCard,
  Deposit,
  Wallet,
  Paypal,
  Bank,
  TokenPurchase,
  TokenConvert,
  Refund,
} from "./graphics";
import {
  STATUS_ICON_MAP,
  localizeTransactionStatus,
} from "../utils";
import { getLocaleTzAbbrFromDate } from "utils";

import { FormattedMoney } from "components/FormattedMoney";

const ICON_MAP = {
  wallet: Wallet,
  rebilly: CreditCard,
  paypal: Paypal,
};

/**
 * TxnHeader - Presentational component for displaying a quick overview of what's in the transaction
 * @param {object} props
 * @param {TxnVariant} props.transaction
 * @returns JSX.Element
 */
export default function TxnHeader({ transaction, accountId }) {
  const { formatMessage, formatDate, formatTime } = useIntl();
  const { transaction_type, transaction_data, transaction_date, transaction_status } = transaction;
  const txnDate = new Date(transaction_date);
  const date =
    (isToday(txnDate) &&
      formatMessage({
        defaultMessage: "today",
        description: "Day",
      })) ||
    (isYesterday(txnDate) &&
      formatMessage({
        defaultMessage: "yesterday",
        description: "Day",
      })) ||
    formatDate(txnDate, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const tz = getLocaleTzAbbrFromDate(txnDate);
  const time = `${formatTime(txnDate)}${tz ? ` (${tz})` : ""}`;
  let datetime = formatMessage(
    {
      defaultMessage: "{date} at {time}",
      description: "Date and time message. Example: 12/1/2020 at 8:01PM",
    },
    {
      date,
      time,
    },
  );
  let TopIcon = null;
  let title = null;
  let status = null;
  let statusIcon = null;
  let isTodayOrYesterday;
  let transferItems;
  let transferItem;
  let wallets;
  let wallet;
  let walletId;
  let amount;

  /**
   * We need to calculate which FULL localized message to use rather than concatenate messages.
   * Concatenated messages should not be used for localized messages.
   * @param {boolean} isTodayOrYesterday
   * @returns Formatted message
   */
  function makeDepositDateTimeMessage(isTodayOrYesterday) {
    const txnStatus =
      transaction_status === "pending"
        ? "Requested"
        : transaction_status === "processed"
          ? "Completed"
          : "";
    if (isTodayOrYesterday) {
      switch (txnStatus) {
        case "Requested":
          return formatMessage(
            {
              defaultMessage: "Requested {todayOrYesterday} at {time}",
              description: "Example: Requested today at 8:01PM",
            },
            {
              todayOrYesterday: date,
              time,
            },
          );
        case "Completed":
          return formatMessage(
            {
              defaultMessage: "Completed {todayOrYesterday} at {time}",
              description: "Example: Completed today at 8:01PM",
            },
            {
              todayOrYesterday: date,
              time,
            },
          );
        case "":
        default:
          return datetime;
      }
    } else {
      switch (txnStatus) {
        case "Requested":
          return formatMessage(
            {
              defaultMessage: "Requested on {date} at {time}",
              description: "Example: Requested on 12/1/2021 at 8:01PM",
            },
            {
              date,
              time,
            },
          );
        case "Completed":
          return formatMessage(
            {
              defaultMessage: "Completed on {date} at {time}",
              description: "Example: Completed on 12/1/2021 at 8:01PM",
            },
            {
              date,
              time,
            },
          );
        case "":
        default:
          return formatMessage(
            {
              defaultMessage: "on {date} at {time}",
              description: "Example: on 12/1/2021 at 8:01PM",
            },
            {
              date,
              time,
            },
          );
      }
    }
  }

  switch (transaction_type) {
    case "balance_transfer":
      switch (transaction.transaction_data.balance_transfer_type) {
        case "merge_account":
          transferItems = transaction_data.transfer_items;
          transferItem = transferItems[0];
          wallets = transaction_data.wallets;

          // assume it's the destination wallet ID
          walletId = transferItem.destination_wallet_id;

          // if the current user is the source user then use the source wallet ID
          if (transferItem.source_account_id === accountId) {
            walletId = transferItem.source_wallet_id;
          }

          wallet = wallets[walletId];
          amount = transferItem.display_amount;

          const MergeAccountBalanceTransferType =
            wallet.net_change > 0 ? (
              <FormattedMessage
                defaultMessage="Credit of <b>{amount}</b>"
                description="Statement with money amount"
                values={{
                  amount,
                  b: (msg) => <Text as="b">{msg}</Text>,
                }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="Debit of <b>{amount}</b>"
                description="Statement with money amount"
                values={{
                  amount,
                  b: (msg) => <Text as="b">{msg}</Text>,
                }}
              />
            );

          isTodayOrYesterday = isToday(txnDate) || isYesterday(txnDate);
          datetime = makeDepositDateTimeMessage(isTodayOrYesterday);
          TopIcon = Wallet;
          title = <>{MergeAccountBalanceTransferType}</>;
          statusIcon = STATUS_ICON_MAP[transaction_status];
          status = (
            <Flex data-testid="status" align="center">
              <Text fontSize="sm" fontWeight={700}>
                <FormattedMessage defaultMessage="Status:" description="Field label" />
                &nbsp;&nbsp;
              </Text>
              <Icon
                mr={1}
                color={transaction_status === "failed" ? "errorText" : "integrator.500"}
                as={statusIcon}
              />
              <Text fontSize="sm" textTransform="capitalize">
                {transaction_status}
              </Text>
            </Flex>
          );
          break;

        case "fund_user":
          transferItems = transaction_data.transfer_items; // @type FundUserBalanceTransferType
          amount = transferItems[0].display_amount;
          isTodayOrYesterday = isToday(txnDate) || isYesterday(txnDate);
          datetime = makeDepositDateTimeMessage(isTodayOrYesterday);
          TopIcon = Wallet;
          title = (
            <FormattedMessage
              defaultMessage="Credit of <b>{amount}</b>"
              description="Statement with money amount"
              values={{
                amount,
                b: (msg) => <Text as="b">{msg}</Text>,
              }}
            />
          );
          statusIcon = STATUS_ICON_MAP[transaction_status];
          status = (
            <Flex data-testid="status" align="center">
              <Text fontSize="sm" fontWeight={700}>
                <FormattedMessage defaultMessage="Status:" description="Field label" />
                &nbsp;&nbsp;
              </Text>
              <Icon
                mr={1}
                color={transaction_status === "failed" ? "errorText" : "integrator.500"}
                as={statusIcon}
              />
              <Text fontSize="sm" textTransform="capitalize">
                {transaction_status}
              </Text>
            </Flex>
          );
          break;
        case "token_convert_payout_reversal":
          transferItems = transaction_data.transfer_items; // @type FundUserBalanceTransferType
          amount = transferItems.find(
            (ti) => ti.subitem_type === "token_convert_virtual_debit_reversal",
          ).display_amount;
          isTodayOrYesterday = isToday(txnDate) || isYesterday(txnDate);
          datetime = makeDepositDateTimeMessage(isTodayOrYesterday);
          TopIcon = TokenPurchase;
          title = (
            <FormattedMessage
              defaultMessage="Token credit of <b>{amount}</b>"
              description="Statement with money amount"
              values={{
                amount,
                b: (msg) => <Text as="b">{msg}</Text>,
              }}
            />
          );
          statusIcon = STATUS_ICON_MAP[transaction_status];
          status = (
            <Flex data-testid="status" align="center">
              <Text fontSize="sm" fontWeight={700}>
                <FormattedMessage defaultMessage="Status:" description="Field label" />
                &nbsp;&nbsp;
              </Text>
              <Icon
                mr={1}
                color={transaction_status === "failed" ? "errorText" : "integrator.500"}
                as={statusIcon}
              />
              <Text fontSize="sm" textTransform="capitalize">
                {transaction_status}
              </Text>
            </Flex>
          );
          break;
        default:
          /**
           * If this is an unsupported transaction_type,
           * we return null.  It's up to whatever
           * is wrapping this component to show appropriate
           * messaging about non-supported transaction types.
           */
          return null;
      }
      break;

    case "deposit":
      const { deposit } = transaction_data; // @type RefundBuyerData
      isTodayOrYesterday = isToday(txnDate) || isYesterday(txnDate);
      datetime = makeDepositDateTimeMessage(isTodayOrYesterday);

      TopIcon = Deposit;
      title = (
        <FormattedMessage
          defaultMessage="Deposit of <b>{amount}</b>"
          description="Statement with money amount"
          values={{
            amount: (
              <FormattedMoney value={deposit.request_amount} currency={deposit.request_currency} />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      statusIcon = STATUS_ICON_MAP[transaction_status];
      status = (
        <Flex data-testid="status" align="center">
          <Text fontSize="sm" fontWeight={700}>
            <FormattedMessage defaultMessage="Status:" description="Field label" />
            &nbsp;&nbsp;
          </Text>
          <Icon
            mr={1}
            color={transaction_status === "failed" ? "errorText" : "integrator.500"}
            as={statusIcon}
          />
          <Text fontSize="sm" textTransform="capitalize">
            {transaction_status}
          </Text>
        </Flex>
      );
      break;
    case "user_purchase":
    case "user_purchase_escrow":
      const { payment_methods, summary } = transaction_data;
      const firstProvider = Object.values(payment_methods)[0]?.provider || "wallet"; // default to wallet if no provider
      TopIcon = ICON_MAP[firstProvider] || Wallet; // default to wallet if we got an unknown provider
      title = (
        <FormattedMessage
          defaultMessage="You paid <b>{amount}</b>"
          description="Example: You paid <b>USD $5.01</b>"
          values={{
            amount: (
              <FormattedMoney as="b" value={summary?.total_amount} currency={summary?.currency} />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      break;
    case "user_purchase_recipient":
      TopIcon = Wallet;
      title = (
        <FormattedMessage
          defaultMessage="You received <b>{amount}</b>"
          description="Example: You received <b>USD $5.01</b>"
          values={{
            amount: (
              <FormattedMoney
                value={transaction_data?.total_received_less_fees}
                currency={transaction_data?.currency}
              />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      break;
    case "payout":
      TopIcon = Bank;
      title = (
        <FormattedMessage
          defaultMessage="Payout of <b>{amount}</b>"
          description="Example: Payout of <b>USD $5.01</b>"
          values={{
            amount: (
              <FormattedMoney
                value={transaction_data?.payout_total}
                currency={transaction_data?.payout_total_currency || transaction_data?.currency}
              />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      isTodayOrYesterday = isToday(txnDate) || isYesterday(txnDate);
      datetime = isTodayOrYesterday
        ? formatMessage(
            {
              defaultMessage: "Requested {todayOrYesterday} at {time}",
              description: "Example: Requested today at 8:01PM",
            },
            {
              todayOrYesterday: date,
              time,
            },
          )
        : formatMessage(
            {
              defaultMessage: "Requested on {date} at {time}",
              description: "Example: Requested on 12/1/2021 at 8:01PM",
            },
            {
              date,
              time,
            },
          );
      statusIcon = STATUS_ICON_MAP[transaction_status];
      status = (
        <Flex data-testid="status" align="center">
          <Text fontSize="sm" fontWeight={700}>
            <FormattedMessage defaultMessage="Status:" description="Field label" />
            &nbsp;&nbsp;
          </Text>
          <Icon
            mr={1}
            color={transaction_status === "failed" ? "errorText" : "integrator.500"}
            as={statusIcon}
          />
          <Text fontSize="sm" textTransform="capitalize">
            {localizeTransactionStatus(transaction, formatMessage)}
          </Text>
        </Flex>
      );
      break;
    case "token_purchase":
      const { token_exchange } = transaction_data; // @type TokenPurchaseData
      TopIcon = TokenPurchase;
      title = (
        <FormattedMessage
          defaultMessage="You purchased <b>{amount}</b>"
          description="Example: You purchased <b>USD $5.01</b>"
          values={{
            amount: (
              <FormattedMoney
                value={token_exchange?.user_received_amount}
                currency={token_exchange?.user_received_currency}
              />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      break;
    case "token_convert":
      const {
        total_amount: tokenConvertTotalAmount,
        currency: tokenConvertCurrency,
        token_exchange: tokenConvertTokenExchange,
      } = transaction_data; // @type TokenPurchaseData
      TopIcon = TokenConvert;
      title = (
        <FormattedMessage
          defaultMessage="<b>{currencyAmount}</b> converted to <b>{tokenAmount}</b>"
          description=""
          values={{
            currencyAmount: (
              <FormattedMoney value={tokenConvertTotalAmount} currency={tokenConvertCurrency} />
            ),
            tokenAmount: (
              <FormattedMoney
                value={tokenConvertTokenExchange.user_received_amount}
                currency={tokenConvertTokenExchange.user_received_currency}
              />
            ),
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      break;
    case "refund":
      const { total_amount: refundTotalAmount, amount_currency } = transaction_data; // @type RefundBuyerData
      TopIcon = Refund;
      title = (
        <FormattedMessage
          defaultMessage="Refund of <b>{amount}</b>"
          description="Example: Refund of <b>USD $5.01</b>"
          values={{
            amount: <FormattedMoney value={refundTotalAmount} currency={amount_currency} />,
            b: (msg) => <Text as="b">{msg}</Text>,
          }}
        />
      );
      break;
    default:
      /**
       * If this is an unsupported transaction_type,
       * we return null.  It's up to whatever
       * is wrapping this component to show appropriate
       * messaging about non-supported transaction types.
       */
      return null;
  }
  return (
    <Flex direction="column" align="center">
      <Box minHeight="60px">
        {TopIcon ? (
          <Suspense fallback={<Loading />}>
            <TopIcon />
          </Suspense>
        ) : null}
      </Box>
      <Box textAlign="center">
        <Text mt={2} fontSize="xl">
          {title}
        </Text>
        <Text pb={4} fontSize="sm" color="tertiaryText">
          {datetime}
        </Text>
      </Box>
      {status}
    </Flex>
  );
}
